/**
 * Schedule class managing a collection of categories
 */

import { Project } from "./Project";
import { Category } from "./Category";
import { EffortContainerBase } from "./EffortContainerBase";

/**
 * Schedule class representing the top-level container for categories
 * Extends EffortContainerBase to inherit common aggregation logic
 */
export class Schedule extends EffortContainerBase {
  private categories: Category[];

  constructor(categories: Category[]) {
    super();
    this.categories = categories;
  }

  /**
   * Get all projects across all categories (implements abstract method)
   * @returns Array of all Project instances
   */
  getAllProjects(): Project[] {
    return this.categories.flatMap((category) => category.getAllProjects());
  }

  /**
   * Get all categories
   * @returns Array of Category instances
   */
  getCategories(): Category[] {
    return [...this.categories];
  }

  /**
   * Get flattened list of all categories and subcategories for display
   * Maintains hierarchical order (parent, then children)
   * @returns Array of all categories in display order
   */
  getFlattenedCategories(): Category[] {
    const flattened: Category[] = [];

    const flatten = (category: Category) => {
      flattened.push(category);
      for (const subcat of category.getSubcategories()) {
        flatten(subcat);
      }
    };

    for (const category of this.categories) {
      flatten(category);
    }

    return flattened;
  }

  /**
   * Add a category to the schedule
   * @param category - Category instance
   */
  addCategory(category: Category): void {
    this.categories.push(category);
    this.invalidateCache();
  }

  /**
   * Remove a category from the schedule
   * @param categoryId - ID of category to remove
   */
  removeCategory(categoryId: number): void {
    this.categories = this.categories.filter((c) => c.id !== categoryId);
    this.invalidateCache();
  }

  /**
   * Update a category in the schedule
   * @param category - Updated category instance
   */
  updateCategory(category: Category): void {
    const index = this.categories.findIndex((c) => c.id === category.id);
    if (index !== -1) {
      this.categories[index] = category;
      this.invalidateCache();
    }
  }

  /**
   * Find a category by ID
   * @param categoryId - ID of category to find
   * @returns Category instance or undefined
   */
  findCategory(categoryId: number): Category | undefined {
    return this.categories.find((c) => c.id === categoryId);
  }

  /**
   * Find which category contains a specific project
   * @param projectId - ID of project to find
   * @returns Category containing the project, or undefined
   */
  findCategoryByProjectId(projectId: number): Category | undefined {
    return this.categories.find((category) => category.hasProject(projectId));
  }

  /**
   * Add a project to a specific category
   * @param categoryId - ID of category to add project to
   * @param project - Project instance
   * @returns True if successful, false if category not found
   */
  addProjectToCategory(categoryId: number, project: Project): boolean {
    const category = this.findCategory(categoryId);
    if (category) {
      category.addProject(project);
      this.invalidateCache();
      return true;
    }
    return false;
  }

  /**
   * Remove a project from its category
   * @param projectId - ID of project to remove
   * @returns True if successful, false if project not found
   */
  removeProject(projectId: number): boolean {
    const category = this.findCategoryByProjectId(projectId);
    if (category) {
      category.removeProject(projectId);
      this.invalidateCache();
      return true;
    }
    return false;
  }

  /**
   * Update a project in its category
   * @param project - Updated project instance
   * @returns True if successful, false if project not found
   */
  updateProject(project: Project): boolean {
    const category = this.findCategoryByProjectId(project.id!);
    if (category) {
      category.updateProject(project);
      this.invalidateCache();
      return true;
    }
    return false;
  }

  /**
   * Move a project from one category to another
   * @param projectId - ID of project to move
   * @param targetCategoryId - ID of target category
   * @returns True if successful
   */
  moveProjectToCategory(projectId: number, targetCategoryId: number): boolean {
    const sourceCategory = this.findCategoryByProjectId(projectId);
    const targetCategory = this.findCategory(targetCategoryId);

    if (sourceCategory && targetCategory) {
      const project = sourceCategory.findProject(projectId);
      if (project) {
        sourceCategory.removeProject(projectId);
        targetCategory.addProject(project);
        this.invalidateCache();
        return true;
      }
    }
    return false;
  }

  /**
   * Get the number of categories in the schedule
   * @returns Number of categories
   */
  getCategoryCount(): number {
    return this.categories.length;
  }
}
